<template>
  <v-dialog
    v-model="isOpen"
    max-width="460px"
    persistent
    contentClass="rounded-xl"
  >
    <v-card class="pa-2 rounded-xl">
      <v-card-title class="justify-space-between">
        <span>Calculadora</span>
        <v-btn icon @click="close" aria-label="Fechar">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-text-field
          v-model="expression"
          label="Expressão"
          outlined
          hide-details
          input-class="text-right"
        />

        <!-- Reserve a fixed area for result to avoid layout shift -->
        <div class="result-container mt-3">
          <div class="result-inner">
            <v-alert v-if="error" type="error" text class="w-full mb-0">
              {{ error }}
            </v-alert>

            <div v-else class="value d-flex flex-column align-end">
              <small class="text-overline d-block mb-1">Resultado</small>
              <div class="display">
                {{ approx ? "≈ " : ""
                }}<span v-if="isNumeric">{{ (result || 0) | number }}</span
                ><span v-else>{{ result }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="keyboard mt-4">
          <div class="grid">
            <!-- Row 1 -->
            <v-btn
              class="calc-btn"
              style="grid-column: span 2"
              color="secondary"
              @click="onKey('C')"
              v-bind="btnBaseProps"
              >C</v-btn
            >
            <v-btn
              class="calc-btn"
              color="secondary"
              @click="onKey('←')"
              v-bind="btnBaseProps"
            >
              <v-icon small>mdi-backspace</v-icon>
            </v-btn>
            <v-btn
              class="calc-btn"
              color="primary"
              dark
              @click="onKey('/')"
              v-bind="btnBaseProps"
            >
              /
            </v-btn>
            <v-btn class="calc-btn" @click="onKey('7')" v-bind="btnBaseProps">
              7
            </v-btn>

            <!-- Row 2 -->
            <v-btn class="calc-btn" @click="onKey('8')" v-bind="btnBaseProps">
              8
            </v-btn>
            <v-btn class="calc-btn" @click="onKey('9')" v-bind="btnBaseProps">
              9
            </v-btn>
            <v-btn
              class="calc-btn"
              color="primary"
              dark
              @click="onKey('*')"
              v-bind="btnBaseProps"
            >
              *
            </v-btn>
            <v-btn class="calc-btn" @click="onKey('4')" v-bind="btnBaseProps">
              4
            </v-btn>

            <!-- Row 3 -->
            <v-btn class="calc-btn" @click="onKey('5')" v-bind="btnBaseProps">
              5
            </v-btn>
            <v-btn class="calc-btn" @click="onKey('6')" v-bind="btnBaseProps">
              6
            </v-btn>
            <v-btn
              class="calc-btn"
              color="primary"
              dark
              @click="onKey('-')"
              v-bind="btnBaseProps"
            >
              -
            </v-btn>
            <v-btn class="calc-btn" @click="onKey('1')" v-bind="btnBaseProps">
              1
            </v-btn>

            <!-- Row 4 -->
            <v-btn class="calc-btn" @click="onKey('2')" v-bind="btnBaseProps">
              2
            </v-btn>
            <v-btn class="calc-btn" @click="onKey('3')" v-bind="btnBaseProps">
              3
            </v-btn>
            <v-btn
              class="calc-btn"
              color="primary"
              dark
              @click="onKey('+')"
              v-bind="btnBaseProps"
            >
              +
            </v-btn>
            <v-btn class="calc-btn" @click="onKey('.')" v-bind="btnBaseProps">
              .
            </v-btn>

            <!-- Row 5 -->
            <v-btn class="calc-btn" @click="onKey('0')" v-bind="btnBaseProps">
              0
            </v-btn>
            <v-btn
              class="calc-btn"
              color="secondary"
              dark
              @click="evaluate(true)"
              v-bind="btnBaseProps"
            >
              =
            </v-btn>
            <v-btn
              class="calc-btn"
              color="red lighten-2"
              dark
              @click="close"
              v-bind="btnBaseProps"
            >
              Fechar
            </v-btn>
          </div>
        </div>
      </v-card-text>

      <!-- removed extra action buttons to keep layout clean -->
    </v-card>
  </v-dialog>
</template>

<script>
import nerdamer from "nerdamer/nerdamer.core";
import debounce from "lodash/debounce";

export default {
  name: "CalculatorModal",
  data: () => ({
    isOpen: false,
    expression: "",
    result: null,
    approx: false,
    error: null,
    btnBaseProps: {
      xLarge: true,
      depressed: true,
      class: "px-1",
    },
  }),
  mounted() {
    // Listen to $root 'calculator' event to open the modal
    this.$root.$on("calculator", this.open);
    // auto evaluate when expression changes (debounced)
    this._autoEval = debounce(() => this.evaluate(), 350);
  },
  beforeDestroy() {
    this.$root.$off("calculator", this.open);
    if (this._autoEval && this._autoEval.cancel) this._autoEval.cancel();
  },
  watch: {
    expression() {
      this.error = null;
      this._autoEval();
    },
    isOpen(val) {
      if (!val) {
        // reset on close
        this.expression = "";
        this.result = null;
        this.error = null;
      }
    },
  },
  computed: {
    isNumeric() {
      if (this.result === null) return false;
      // try parse as float
      return !isNaN(Number(String(this.result).replace(/,/g, ".")));
    },
  },
  methods: {
    open(payload) {
      if (payload && typeof payload === "string") this.expression = payload;
      this.isOpen = true;
      this.$nextTick(() => {
        // focus input if available
        const el = this.$el.querySelector("input");
        if (el) el.focus();
      });
    },
    close() {
      this.isOpen = false;
    },
    onKey(key) {
      if (key === "C") return this.clear();
      if (key === "←") return this.backspace();
      // only basic keys expected
      this.expression += key;
    },
    backspace() {
      this.expression = this.expression.slice(0, -1);
    },
    clear() {
      this.expression = "";
      this.result = null;
      this.error = null;
      this.approx = false;
    },
    evaluate(replaceExpression = false) {
      const expr = String(this.expression || "").trim();
      if (!expr) return (this.result = null);
      try {
        nerdamer.flush();
        const sanitized = expr.replace(/,/g, ".");
        const evaluated = nerdamer(sanitized).evaluate().text();
        this.result = evaluated;
        if (replaceExpression) this.expression = evaluated;
        this.approx = false;
        this.error = null;
      } catch (err) {
        this.result = null;
        this.error = err.message || "Erro ao avaliar expressão";
      }
    },
  },
};
</script>

<style scoped>
.keyboard .row {
  display: flex;
  justify-content: space-between;
}
.display {
  font-size: 1.25rem;
  font-weight: 600;
}
.error {
  font-size: 0.9rem;
}

.keyboard .grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}
.calc-btn {
  height: 44px;
  min-width: 56px;
  width: 100%;
}

.result-container {
  min-height: 64px; /* reserve space to avoid layout shift */
}
.result-inner {
  display: flex;
  flex-direction: column;
  align-items: flex-end; /* right align content */
}
.display {
  font-size: 1.25rem;
  font-weight: 600;
  text-align: right;
  width: 100%;
}
</style>
